import React, { useCallback } from 'react';
import { AutoAwesome } from '@mui/icons-material';
import './SpellCard.css';
import './SpellView.css';
import './BestWeaponCard.css';
import CriterionWeight from '../generator/CriterionWeight';
import DropdownMenu from '../utils/DropdownMenu';
import Tooltip from '../utils/Tooltip';
import {
    BEST_WEAPON_METRICS,
    bestWeaponMetricLabel,
    bestWeaponValue,
} from '../../core/bestWeaponMetric';
import { WEAPON_GROUP_IMG } from '../../core/buildUtils';

/**
 * "Find the best weapon" — the weapon criterion that names no weapon.
 *
 * Its own card rather than a second row on the weapon's, because it is not
 * about the weapon in the slot: it asks the solver to go and choose one, and
 * the two are mutually exclusive. It names no item, so where a spell draws its
 * art it draws weapons as a kind — and it is a `spell-card` otherwise,
 * padding, hover wash and all, because it sits in that grid, answers a hover
 * with a panel of its own, and should not read as a different kind of thing.
 */
// A criterion row is a line of a grid, so the label is short; the sentence it
// is short for is the tooltip's title, and the tooltip says the rest.
const TXT_LABEL = {
    fr: 'Arme auto.',
    en: 'Auto weapon',
    de: 'Auto-Waffe',
    es: 'Arma auto',
    pt: 'Arma auto',
};
const TXT_TITLE = {
    fr: 'Arme automatique',
    en: 'Automatic weapon',
    de: 'Automatische Waffe',
    es: 'Arma automática',
    pt: 'Arma automática',
};
// One sentence: what the generator looks for, that forgemage counts towards it
// — which is the part nobody would assume — and that the rest of the request
// still holds while it does.
const TXT_ABOUT = {
    fr: "Le générateur cherche l'arme qui fait le plus de dégâts, forgemagie comprise, tout en tenant compte de vos autres critères.",
    en: 'The generator looks for the weapon that does the most damage, maging included, while still taking your other criteria into account.',
    de: 'Der Generator sucht die Waffe mit dem meisten Schaden — Schmiedemagie eingeschlossen — und berücksichtigt dabei weiterhin deine übrigen Kriterien.',
    es: 'El generador busca el arma que más daño hace, forjamagia incluida, sin dejar de tener en cuenta tus demás criterios.',
    pt: 'O gerador procura a arma que causa mais dano, forjamagia incluída, sem deixar de ter em conta os seus outros critérios.',
};

const EMPTY_VALUE = '—';

const BestWeaponCard = ({
    /** The weapon card row for whatever is in the slot — what a run just found. */
    weapon = null,
    lang = 'fr',
    selected = false,
    disabled = false,
    selectable = true,
    onToggle,
    metric = BEST_WEAPON_METRICS[0],
    onMetricChange,
    showWeight = false,
    weight = 1,
    onWeightChange,
}) => {
    const canToggle = selectable && !disabled && !!onToggle;
    // The value the criterion scores, through the helper the kernel mirrors.
    const value = weapon && weapon.apCost > 0
        ? Math.round(bestWeaponValue(weapon, metric))
        : null;

    const handleClick = useCallback((e) => {
        e.preventDefault();
        if (canToggle) onToggle();
    }, [canToggle, onToggle]);

    const handleKeyDown = useCallback((e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        if (canToggle) onToggle();
    }, [canToggle, onToggle]);

    const rootClassName = [
        'spell-card',
        'best-weapon-card',
        disabled ? 'spell-card--disabled' : '',
        selectable ? '' : 'spell-card--unselectable',
    ].filter(Boolean).join(' ');

    const rowClassName = [
        'spell-view-row',
        'spell-view-row-first',
        canToggle ? 'spell-view-row--selectable' : '',
        selected ? 'spell-view-row--selected' : '',
    ].filter(Boolean).join(' ');

    // Styled as the automatic-defense tooltip is, minus its diagram: there is
    // no split to draw here, only what the criterion does.
    const about = (
        <div className="best-weapon-about">
            <h6 className="best-weapon-about__title">{TXT_TITLE[lang] || TXT_TITLE.en}</h6>
            <span className="best-weapon-about__text">{TXT_ABOUT[lang] || TXT_ABOUT.en}</span>
        </div>
    );

    return (
        <div className={rootClassName}>
            <Tooltip content={about}>
                <div className="spell-card-trigger">
                    <div className={`spell-view${selectable ? '' : ' spell-view--readonly'}`}>
                        <div className="spell-view-compact-icon">
                            <span className="best-weapon-card-icon" aria-hidden="true">
                                <img className="best-weapon-card-icon-art" src={WEAPON_GROUP_IMG} alt="" />
                                <AutoAwesome className="best-weapon-card-icon-spark" />
                            </span>
                        </div>
                        <div className="spell-view-compact-rows">
                            <div
                                className={rowClassName}
                                role={canToggle ? 'button' : undefined}
                                tabIndex={canToggle ? 0 : undefined}
                                aria-pressed={canToggle ? selected : undefined}
                                onClick={canToggle ? handleClick : undefined}
                                onKeyDown={canToggle ? handleKeyDown : undefined}
                            >
                                <div className="spell-view-name">{TXT_LABEL[lang] || TXT_LABEL.en}</div>
                                {onMetricChange ? (
                                    // Its own control inside a row that is
                                    // itself a button: the click must not also
                                    // toggle the criterion off.
                                    <span
                                        className="best-weapon-card-metric"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        role="presentation"
                                    >
                                        <DropdownMenu
                                            items={BEST_WEAPON_METRICS.map((m) => ({
                                                // The dropdown names the unit,
                                                // which is why the number
                                                // beside it carries none.
                                                label: bestWeaponMetricLabel(m, lang),
                                                onClick: () => onMetricChange(m),
                                            }))}
                                            activeIndex={Math.max(0, BEST_WEAPON_METRICS.indexOf(metric))}
                                            disabled={disabled || !selectable}
                                            dropdownClassName="best-weapon-card-metric-dropdown"
                                        />
                                    </span>
                                ) : null}
                                <div className="spell-view-val-cell">
                                    <span className="spell-view-val">
                                        {value != null ? value : EMPTY_VALUE}
                                    </span>
                                </div>
                                {showWeight ? (
                                    <div className="spell-view-weight-cell">
                                        {selected ? (
                                            <CriterionWeight
                                                value={weight}
                                                onChange={onWeightChange}
                                                disabled={disabled || !selectable}
                                                lang={lang}
                                            />
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};

export default BestWeaponCard;
